import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Phone, MessageCircle, X } from 'react-feather';
import { Logo } from './Logo';
import { FaBars } from 'react-icons/fa';

const WHATSAPP_NUMBER = '256782692271';
const PHONE_NUMBER = '+256782692271';

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { label: 'Shop', to: '/' },
    { label: 'Custom', to: '/custom' },
    { label: 'Playtime', to: '/play-time' },
    { label: 'About', to: '/about' },
    { label: 'Contact', to: '/contact' },
  ];

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const linkBase = 'inline-flex items-center px-3 py-2 text-sm font-medium transition-colors';
  const linkInactive = 'text-gray-800 hover:font-semibold';
  const linkActive =
    'text-gray-800 after:ml-2 after:h-1 after:w-1.5 after:rounded-full after:bg-[#12A2BF]';

  return (
    <nav className="fixed inset-x-0 top-0 z-50 bg-white text-gray-700 shadow-sm">
      {/* Top bar */}
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        {/* Brand */}
        <div className="flex items-center gap-3">
          <Link to="/" aria-label="Gambit Games Uganda" className="flex items-center gap-3">
            {Logo ? (
              <Logo className="h-8 w-auto" />
            ) : (
              <span className="text-lg font-bold tracking-tight text-gray-900">
                Gambit <span className="text-[#12A2BF]">Games</span>
              </span>
            )}
          </Link>
          <span className="hidden border-l border-gray-200 pl-3 text-xs text-gray-500 sm:inline">
            Handcrafted board games.
          </span>
        </div>

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
            >
              {link.label}
            </NavLink>
          ))}
          {/* CTA: WhatsApp Order */}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noreferrer"
            className="ml-3 inline-flex items-center gap-2 rounded-full bg-[#12A2BF] px-4 py-2 text-sm font-semibold text-white shadow hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#12A2BF] focus:ring-offset-2 focus:ring-offset-white"
          >
            <MessageCircle size={16} />
            Order via WhatsApp
          </a>
        </div>

        {/* Mobile actions */}
        <div className="flex items-center gap-2 md:hidden">
          <a
            href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}
            className="rounded-full p-2 text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#12A2BF]"
            aria-label="Call us"
          >
            <Phone size={18} />
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#12A2BF]"
            aria-label="Open menu"
          >
            <FaBars className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Mobile Drawer (Dialog) */}
      <Transition show={mobileOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[60] md:hidden" onClose={setMobileOpen}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          {/* Panel */}
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition duration-300 ease-out"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition duration-200 ease-in"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md bg-gray-900 px-6 pb-8 pt-6 shadow-2xl">
                    <div className="mb-6 flex items-center justify-between">
                      <Link
                        to="/"
                        className="text-lg font-semibold text-white"
                        onClick={() => setMobileOpen(false)}
                      >
                        Gambit <span className="text-[#12A2BF]">Games</span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => setMobileOpen(false)}
                        className="rounded-md p-2 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#12A2BF]"
                        aria-label="Close menu"
                      >
                        <X className="h-6 w-6" />
                      </button>
                    </div>

                    <nav className="space-y-1">
                      {navLinks.map((link) => (
                        <NavLink
                          key={link.to}
                          to={link.to}
                          className={({ isActive }) =>
                            `block rounded-lg px-3 py-2 text-base font-medium ${
                              isActive
                                ? 'bg-gray-800 text-white'
                                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                            }`
                          }
                        >
                          {link.label}
                        </NavLink>
                      ))}
                    </nav>

                    <div className="mt-6 space-y-3">
                      <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-center gap-2 rounded-lg bg-[#12A2BF] px-4 py-3 font-semibold text-black hover:brightness-110"
                      >
                        <MessageCircle size={18} />
                        Order via WhatsApp
                      </a>

                      <a
                        href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}
                        className="block rounded-lg border border-gray-700 px-4 py-3 text-center font-medium text-gray-200 hover:bg-gray-800"
                      >
                        Call us: {PHONE_NUMBER}
                      </a>
                    </div>

                    <p className="mt-6 text-xs text-gray-400">
                      Handcrafted wooden board games • Najjera, Kampala
                    </p>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </nav>
  );
}
